import { useState, useEffect, useRef} from 'react';
import { useNavigate, Routes, Route, useLocation, Navigate} from 'react-router-dom';
import { CurrentUserContext } from './context/CurrentUserContext';
import { LanguageProvider } from './context/LanguageContext.js';
import './App.css';
import api from './utils/API/MainApi.js';
import Header from './components/Header/Header';
import Home from './components/Home/Home';
import LoginPopup from './components/LoginPopup/LoginPopup.jsx';
import Signout from './components/Signout/Signout.jsx';
import ForgotPasswordPopup from './components/ForgotPassword/ForgotPassword.jsx';
import ResetPassword from './components/ResetPassword/ResetPassword.jsx';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import Upgrade from './components/Upgrade/Upgrade.jsx';
import CoverPage from './components/Welcome/Welcome.jsx'
import LevelContent from './components/Content/LevelContent.jsx';
import DashboardContent from './components/Content/DashboardContent/DashboardContent.jsx'
import VideosHome from './components/Content/VideosHome/VideosHome.jsx';
import ReaderHome from './components/Content/ReaderHome/ReaderHome.jsx';
import UtilsHome from "./components/UtilsHome/UtilsHome.jsx";
import Utils from "./components/Utils/Ecocalendar/Ecocalendar.jsx"
import GlossarySection from './components/Content/Glossary/Glossary.jsx';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner.jsx';
import { levelContentData } from './components/Content/LevelContentData.js';
import { getAccessibleLevels } from './utils/accessibleLevels.js';
import CourseGateway from './components/MyNotes/MyNotes.jsx';
import Account from './components/MyAccount/Account.jsx';
import Admin from './components/Admin/Admin';
import AdminEditUser from './components/Admin/Components/AdminEditUser/AdminEditUser.jsx';
import Ecocalender from './components/Utils/Ecocalendar/Ecocalendar.jsx';
import AboutUs from './components/AboutUs/AboutUs.jsx';
import Courses from './components/Courses/Courses.jsx';
import PackageGateway from './components/Packages/PackageGateway.jsx';
import Footer from './components/Footer/Footer.jsx';
import Terms from './components/Terms/Terms.jsx';
import Privacy from './components/Privacy/Privacy.jsx';
import ContactUs from './components/Contact/Contact.jsx';
import { useCurrentCourseSlug } from './utils/getCurrentCourseSlug.js';
import CourseLessons from './components/CourseLessons/CourseLessons.jsx';
import CourseContent from './components/CourseContent/CourseContent.jsx';
import CourseEbooks from './components/CourseEbooks/CourseEbooks.jsx';
import MyCourses from './components/MyCourses/MyCourses.jsx';
import LecturesTree from './components/ContentHtml/LecturesHome/LecturesTree.jsx';
import MyHistory from './components/MyHistory/MyHistory.jsx';
import EbooksTree from './components/ContentHtml/ReaderHome/EbooksTree.jsx';
import VideosTree from "./components/Content/VideosHome/VideosTree.jsx";

function App() {
  const pagesRef = useRef(null);
  const navigate = useNavigate();
  const courseSlug = useCurrentCourseSlug();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [isSignOutPopupOpen, setIsSignOutPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState({});
  const [courseProgress, setCourseProgress] = useState([]);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [notesUpdated, setNotesUpdated] = useState(0);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({top: 0, left: 0, behavior: "instant"});
  }, [location.pathname]);

  useEffect(() => {
    if (!courseSlug || !isLoggedIn) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const API_URL =
      window.location.hostname === "localhost"
        ? "http://localhost:4000/api"
        : process.env.REACT_APP_API_URL;

    fetch(`${API_URL}/progress/me/${courseSlug}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error("Failed to load course progress");
        }
        return res.json();
      })
      .then(data => {
        console.log("APP COURSE PROGRESS:", data.progress);
        setCourseProgress(data.progress || []);
      })
      .catch(err => {
        console.error("Failed loading course progress:", err);
      });
  }, [courseSlug, isLoggedIn]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }
    api.getMe(token)
      .then((user) => {
        const normalizedUser = {
          ...user,
          _id: user._id || user.id,
        };
        setCurrentUser(normalizedUser);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setIsLoggedIn(true);
        if (window.location.pathname === '/') {
        if (normalizedUser.subscriptionLevel === 9999) {
            navigate('/admin');
          } else {
            navigate(`/course/${courseSlug}/welcome`);
          }
        }
      })
      .catch((err) => {
        console.error("Token check failed:", err);
        localStorage.removeItem('token');
        localStorage.removeItem("user");
        localStorage.removeItem("activeCourse");
        setIsLoggedIn(false);
        setCurrentUser({ name: "guest", subscriptionLevel: 0 });
      })
      .finally(() => {
        setTimeout(() => setIsLoading(false), 750);
      });
  }, []);
  

  function handleOpenLoginPopupDesktop() { 
    setIsLoginPopupOpen(true);   }
  
  function handleOpenSignupPopupDesktop() {
    setIsSignOutPopupOpen(true); }

  function handleForgotPasswordClick() {
    setIsForgotPasswordOpen(true);     
    setIsLoginPopupOpen(false);        
  };


  function handleClosePopup() {  
    setIsLoginPopupOpen(false); 
    setIsSignOutPopupOpen(false);
    setIsForgotPasswordOpen(false);
  }

  function handleLogin({ email, password }) {
    api.login({ email, password })
      .then(data => {
        const token = data.token;
        localStorage.setItem('token', token);
        return api.getMe(token);
      })
      .then(user => {
        const normalizedUser = { ...user, _id: user._id || user.id };

        setCurrentUser(normalizedUser);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setIsLoggedIn(true);
        setIsLoginPopupOpen(false);

        //const courseSlug = normalizedUser.courseSlug || "fundamentals" ; //users may not have fundamentals so we will add || html later after user modal update

        const firstCourseSlug  = normalizedUser.courses?.[0]?.courseSlug || localStorage.getItem("activeCourse") || "fundamentals"

        navigate(
          normalizedUser.subscriptionLevel === 9999
          ? "/admin"
          : `/course/${firstCourseSlug}/myCourses`
        )
        })
        .catch(err => {
          console.error('Login error:', err.message);
        });
    }
    
  function handleSignOut() {
    localStorage.removeItem('token');
    localStorage.removeItem("user");
    localStorage.removeItem("activeCourse");
    setIsLoggedIn(false);
    setCurrentUser({ name: "guest", subscriptionLevel: 0});
    navigate('/'); 
  }
 
  function handleEditProfile() {
    console.log("Edit Profile clicked");
  }
  
  function handleChangePassword() {
    console.log("Change Password clicked");
  }


  return (
    <LanguageProvider>
      <CurrentUserContext.Provider value={{ currentUser, setCurrentUser, notesUpdated, setNotesUpdated}}>
        <div className="App">
          <Header
            isLoggedIn={isLoggedIn}
            currentUser={currentUser}
            isLoginPopupOpen={isLoginPopupOpen} 
            onSignInPopupClick={handleOpenLoginPopupDesktop}
            onMobilePopupClick={() => setIsLoginPopupOpen(true)}
            onMobilePopupMenu={() => {}}
            onSignOutPopupClick={handleOpenSignupPopupDesktop}
            onMobileSignoutClick={() => setIsSignOutPopupOpen(true)}
            onMobileSignoutMenu={() => {}}
          />
          <LoginPopup 
            isOpen={isLoginPopupOpen}
            onClose={handleClosePopup}
            onLogin={handleLogin}
            handleForgotPasswordClick={handleForgotPasswordClick}
          />
          <ForgotPasswordPopup
            isOpen={isForgotPasswordOpen}
            onClose={handleClosePopup}
          />
          
          <Signout
          isOpen={isSignOutPopupOpen}
          onClose={handleClosePopup}
          onSignOut={handleSignOut}
          />
          
    

          <div className="pages" ref={pagesRef}>
            
            {/* development only*/}
          {/*  <div style={{ padding: "1rem", textAlign: "center" }}>
            <label style={{ marginRight: "0.5rem" }}>Switch Test User:</label>
            <select onChange={(e) => setSelectedUserKey(e.target.value)} value={selectedUserKey}>
              <option value="guest">Guest</option>
              <option value="basic">Basic</option>
              <option value="intermediate">Premium</option>
              <option value="admin">Admin</option>
            </select>   // to creat hard codeed to allow the use to see the courses he have 
          </div> */}


            {isLoading ?  <LoadingSpinner/> : (
              <Routes>
                <Route path="/" element={<Home user={currentUser} />} />
                <Route path="/upgrade" element={<Upgrade />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/course/:courseSlug/welcome"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <CoverPage/>
                    </ProtectedRoute>
                  } 
                />
                <Route path="/aboutus" element={<AboutUs />}/>
                <Route path="/courses" element={<Courses />}/>
                <Route path="/course/:courseSlug/packages" element={<PackageGateway />}/>
                <Route path="/terms" element={<Terms />}/>
                <Route path="/privacy" element={<Privacy />}/>
                <Route path="/contactus" element={<ContactUs />}/>
                

                {/*<Route path="/onboarding" 
                    element={
                      <ProtectedRoute user={currentuser} isLoggedin={isLoggedIn} requiredLevel={100} onRequireLogin={() => setIsLoginPopupOpen(true)}>
                      { currentUser.role === 999
                       ?  <Navigate to="/admin" replace />
                       :  <Onboarding user={currentUser} setCurrentUser={setCurrentUser} />
                      }
                    }
                */}
                <Route path="course/:courseSlug/content"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}   onRequireLogin={() => setIsLoginPopupOpen(true)}>
                      { currentUser.role === 9999 
                      ? <Navigate to="/admin" replace />
                      : <CourseContent user={currentUser} setCurrentUser={setCurrentUser} 
                                       courseProgress={courseProgress} setCourseProgress={setCourseProgress} />
                      }
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="course/:courseSlug/dashboard" 
                  element={
                    <ProtectedRoute user={currentUser} 
                    isLoggedIn={isLoggedIn}  
                    requiredLevel={100}>
                      <DashboardContent user={currentUser} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/course/:courseSlug/videos"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <VideosHome user={currentUser}
                        accessibleLevels={getAccessibleLevels(currentUser)} />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/course/:courseSlug/videosTree"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <VideosTree user={currentUser} />
                    </ProtectedRoute>
                  }
                />
                <Route path="/course/:courseSlug/mycourses"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <MyCourses />
                    </ProtectedRoute>
                  }
                />
                <Route path="/course/:courseSlug/history"
                  element={<MyHistory courseProgress={courseProgress} />}
                /> 
                <Route
                  path="/course/:courseSlug/videos/:videoSlug"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <VideosHome
                        user={currentUser}
                        accessibleLevels={getAccessibleLevels(currentUser)}
                      />
                    </ProtectedRoute>
                  }/>

               <Route
                  path="/course/:courseSlug/lectures"
                  element={
                    <ProtectedRoute
                      user={currentUser}
                      isLoggedIn={isLoggedIn}
                      requiredLevel={100}
                    >
                      <CourseLessons
                        user={currentUser}
                        accessibleLevels={getAccessibleLevels(currentUser)}
                        courseProgress={courseProgress}
                        setCourseProgress={setCourseProgress}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/course/:courseSlug/lecturesTree"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn}requiredLevel={100}>
                      <LecturesTree user={currentUser} courseProgress={courseProgress}/>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/course/:courseSlug/lectures/:lectureSlug"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <CourseLessons user={currentUser} accessibleLevels={getAccessibleLevels(currentUser)} 
                                     courseProgress={courseProgress} setCourseProgress={setCourseProgress} />
                    </ProtectedRoute>
                  }
                />
                
{/*                 <Route
                  path="course/:courseSlug/docs/:pdfSlug" element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <CourseEbooks user={currentUser} 
                      accessibleLevels={getAccessibleLevels(currentUser)}
                      />
                    </ProtectedRoute>
                  }
                /> */}
          {/*       <Route
                  path="course/:courseSlug/docs/*" element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <CourseEbooks user={currentUser} 
                      accessibleLevels={getAccessibleLevels(currentUser)}
                      />
                    </ProtectedRoute>
                  }
                /> */}
                <Route path="course/:courseSlug/docs/:pdfSlug?" element={
                  <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                    <CourseEbooks 
                      user={currentUser} accessibleLevels={getAccessibleLevels(currentUser)} />
                  </ProtectedRoute>
                }>
                </Route>

                <Route path="/course/:courseSlug/ebooksTree"
                  element={
                    <ProtectedRoute
                      user={currentUser}
                      isLoggedIn={isLoggedIn}
                      requiredLevel={100}
                    >
                      <EbooksTree />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="course/:courseSlug/utils/" element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <UtilsHome user={currentUser} />
                      {/* <Ecocalender /> */}
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="course/:courseSlug/utils/glossary" element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <GlossarySection/>
                    </ProtectedRoute>
                  }
                />
                <Route 
                  path="course/:courseSlug/notes" element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <CourseGateway />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="course/:courseSlug/account" element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={100}>
                      <Account user={currentUser} setCurrentUser={setCurrentUser} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={9999}>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={9999}>
                      <Admin />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users/:id"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={9999}>
                      <AdminEditUser />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/content/:courseSlug"
                  element={
                    <ProtectedRoute user={currentUser} isLoggedIn={isLoggedIn} requiredLevel={9999}>
                      <CourseContent user={{ ...currentUser, subscriptionLevel: 9999 }}  isAdminPreview={true}/>
                    </ProtectedRoute>
                  }
                />
              </Routes>
              

            )}
          </div>
          <Footer />
        </div>
      </CurrentUserContext.Provider>
    </LanguageProvider>
  );
}

export default App;
